import { getCookie, setCookie } from 'cookies-next'
import { add } from 'date-fns'
import { cookieKeys, localStorageKeys } from 'lib/consts/browserStorage'
import { getJtiFromToken } from 'lib/helpers/getJtiFromToken/getJtiFromToken'
import { getLSItem } from 'lib/helpers/getLSItem/getLSItem'
import { logError } from 'lib/tracking/datadog'

// How the SDK should adopt the resolved id: keep it (resume), fold the prior anonymous session into
// it (add), or stop the prior session and start fresh (replace).
type DeviceSessionAction = 'resume' | 'add' | 'replace'

interface ResolvedDeviceSession {
  id: string
  action: DeviceSessionAction
}

const ANONYMOUS_OWNER = 'anonymous'

// Socure caps customerSessionId at 64 chars; the backend's jti (SecureRandom.hex(32)) is exactly 64,
// so a longer value means the token contract changed and must not silently mis-key Socure.
const SOCURE_SESSION_ID_MAX_LENGTH = 64

/**
 * Resolves the per-login Socure device session id for the given auth token (`null` when anonymous).
 *
 * For an authenticated session the id IS the token's `jti` claim — the same value the backend mints
 * as `jwt_id` and sends Oscilar as `session_key`, so Socure indexes device data under the key Oscilar
 * correlates by. Anonymous activity (or an undecodable token) gets a throwaway random id; keying the
 * owner on `jti` rather than the raw token also means a token refresh that preserves the `jti` resumes
 * the same session instead of churning a new one.
 *
 * Asymmetry by design: `add` (fold) only when leaving anonymous, never one login → another — folding
 * one login's data into another's id is the cross-login leak we must avoid. Sign-out `replace`s, so
 * the anonymous gap between logins carries no prior authenticated data.
 */
export const resolveDeviceSession = (authToken: Maybe<string>): ResolvedDeviceSession => {
  const decodedJti = authToken ? getJtiFromToken(authToken) : null
  const jti = decodedJti && decodedJti.length <= SOCURE_SESSION_ID_MAX_LENGTH ? decodedJti : null

  if (authToken && !jti) {
    // Degrading to anonymous silently is how the Oscilar correlation broke unnoticed before — be loud.
    logError(new Error('Socure device session degraded to anonymous: auth token jti is missing or unusable'))
  }

  const owner = jti ?? ANONYMOUS_OWNER
  const storedId = getCookie(cookieKeys.socureDeviceSession)
  const storedOwner = getLSItem(localStorageKeys.socureDeviceSessionOwner)

  if (storedId && storedOwner === owner) {
    return { id: `${storedId}`, action: 'resume' }
  }

  const id = jti ?? crypto.randomUUID()

  setCookie(cookieKeys.socureDeviceSession, id, { expires: add(new Date(), { years: 1 }) })
  window.localStorage.setItem(localStorageKeys.socureDeviceSessionOwner, owner)

  const leavingAnonymous = storedOwner === ANONYMOUS_OWNER
  const action: DeviceSessionAction = leavingAnonymous && Boolean(jti) ? 'add' : 'replace'

  return { id, action }
}
